import { useEffect, useRef, useState } from 'react';
import { ordersAPI, OrderList } from '../lib/api';
import { useTraderTime } from '../contexts/TraderTimeContext';

export function useArchivedOrders(orderType: 'deposit' | 'withdraw', dateFrom: string, dateTo: string, status: string) {
    const { offsetMinutes } = useTraderTime();
    const pageSize = 50;
    const filterKey = JSON.stringify([orderType, dateFrom, dateTo, status, offsetMinutes]);
    const [pagination, setPagination] = useState({ key: filterKey, page: 1 });
    const currentPage = pagination.key === filterKey ? pagination.page : 1;
    const [refresh, setRefresh] = useState(0);
    const requestKey = `${filterKey}:${currentPage}:${refresh}`;
    const latestKey = useRef(requestKey);
    latestKey.current = requestKey;
    const [result, setResult] = useState<{ key: string; data?: OrderList; error?: string } | null>(null);

    useEffect(() => {
        setPagination(previous => previous.key === filterKey ? previous : { key: filterKey, page: 1 });
    }, [filterKey]);

    useEffect(() => {
        let active = true;
        if (dateFrom && dateTo && dateFrom > dateTo) {
            setResult({ key: requestKey, error: 'Дата начала периода не может быть позже даты окончания.' });
            return;
        }
        ordersAPI.getMyOrders(currentPage, pageSize, {
            order_type: orderType, archived: true,
            status: status === 'all' ? undefined : status as 'completed' | 'cancelled',
            start_date: dateFrom || undefined, end_date: dateTo || undefined,
        }).then(data => {
            if (!active || latestKey.current !== requestKey) return;
            if (currentPage > Math.max(1, data.pages)) {
                setPagination({ key: filterKey, page: Math.max(1, data.pages) });
            } else setResult({ key: requestKey, data });
        }).catch(() => {
            if (active && latestKey.current === requestKey) setResult({ key: requestKey, error: 'Не удалось загрузить архив' });
        });
        return () => { active = false; };
    }, [requestKey, filterKey, currentPage, dateFrom, dateTo, orderType, status]);

    const visible = result?.key === requestKey ? result : null;
    return {
        filteredOrders: visible?.data?.orders ?? [],
        currentPage, pageSize, totalPages: visible?.data?.pages ?? 1, totalItems: visible?.data?.total ?? 0,
        isLoading: !visible, error: visible?.error ?? null,
        handlePageChange: (page: number) => setPagination({ key: filterKey, page }),
        loadOrders: (_page?: number) => setRefresh(value => value + 1),
    };
}
